import React, { useState, useRef, useEffect } from "react";
import "./HashLab.css"; // reuse the HashLab theme

const API_URL =
  "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection";

// Robust callApi identical style to HashLab
async function callApi(payload) {

  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ method: "proxy", ...payload }),
  });


  const text = await res.text();

  // Try parsing JSON, otherwise return plain text
  let outer;
  try {
    outer = JSON.parse(text);
  } catch {
    return { terminal: `[!] Backend returned non-JSON:\n${text}` };
  }

  let inner;
  try {
    inner = typeof outer.body === "string" ? JSON.parse(outer.body) : outer.body || outer;
  } catch {
    inner = outer.body || outer; // fallback if body isn't JSON
  }

  if (outer.statusCode && outer.statusCode >= 400) {
    throw new Error(inner?.error || "Request failed");
  }
  return inner;
}


export default function ProxyLab() {
  const [url, setUrl] = useState("https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection");
  const [method, setMethod] = useState("GET");
  const [headers, setHeaders] = useState('{ "Content-Type": "application/json" }');
  const [body, setBody] = useState("");
  const [terminal, setTerminal] = useState("Ready.\n");
  const [intercepted, setIntercepted] = useState("");
  const [response, setResponse] = useState("");
  const [isWorking, setIsWorking] = useState(false);

  const terminalRef = useRef(null);
  useEffect(() => {
    if (terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [terminal]);

  // small sample sets
  const sampleHeaders = {
    "JSON API": '{ "Content-Type": "application/json" }',
    "Auth Bearer": '{ "Authorization": "Bearer <token>" }',
  };
  const sampleBodies = {
    "Login JSON": '{ "username": "alice", "password": "1234" }',
    "Empty": "",
  };

  const log = (msg) => setTerminal((t) => t + msg + "\n");

  const handlePickHeader = (k) => {
    setHeaders(sampleHeaders[k]);
    log(`[*] Loaded header sample: ${k}`);
  };
  const handlePickBody = (k) => {
    setBody(sampleBodies[k]);
    log(`[*] Loaded body sample: ${k}`);
  };

  const handleSend = async () => {

    if (!url.trim()) {
      log("[!] Error: URL is empty. Provide a target URL.");
      return;
    }

    let parsedHeaders = {};
    try {
      parsedHeaders = headers.trim() ? JSON.parse(headers) : {};
    } catch (e) {
      log(`[!] Error: Invalid headers JSON — ${e.message}`);
      return;
    }

    // log locally
    log(`> Proxying ${method} ${url}`);
    log(`> Headers: ${headers ? "(see Intercepted panel)" : "none"}`);
    if (body) log(`> Body length: ${body.length} chars`);

    setIsWorking(true);
    try {
      const data = await callApi({
        action: "proxy",
        url,
        method,
        headers: parsedHeaders,
        body,
      });
      if (data.terminal && !data.intercepted && !data.response) {
        // purely non-JSON error from backend
        log(data.terminal);
        return;
      }

      // what's expected from Lambda: { intercepted: {...}, response: {...}, terminal: "..." }
      setIntercepted(JSON.stringify(data.intercepted || data.request || {}, null, 2));
      setResponse(JSON.stringify(data.response || data.target_response || {}, null, 2));

      // append any server-side terminal logs to local terminal
      if (data.terminal) {
        setTerminal((t) => t + data.terminal);
      } else {
        log("✅ Proxy completed. See Intercepted & Response columns.");
      }
    } catch (err) {
      log(`[!] Error proxying: ${err.message}`);
    } finally {
      setIsWorking(false);
    }
  };

  const handleReset = () => {
    setUrl("");
    setMethod("GET");
    setHeaders('{ "Content-Type": "application/json" }');
    setBody("");
    setIntercepted("");
    setResponse("");
    setTerminal("Ready.\n");
    setIsWorking(false);
  };

  return (
    <div className="hashlab-container">
      <h1 className="hashlab-title">🔎 Proxy API Lab</h1>
      <p className="hashlab-sub">Build a request, send via the Proxy Lambda and inspect intercepted traffic.</p>

      <div className="hashlab-grid">
        {/* Left: Request Builder */}
        <div className="panel">
          <h2>Request Builder</h2>

          <label>Target URL</label>
          <input type="text" placeholder="https://api.example.com/login" value={url} onChange={(e) => setUrl(e.target.value)} />

          <label>Method</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option>GET</option>
            <option>POST</option>
            <option>PUT</option>
            <option>DELETE</option>
            <option>PATCH</option>
          </select>

          <label>Headers (JSON)</label>
          <textarea rows={4} value={headers} onChange={(e) => setHeaders(e.target.value)} />

          <div className="sample-lists">
            <div>Header samples:</div>
            <button onClick={() => handlePickHeader("JSON API")}>JSON API</button>
            <button onClick={() => handlePickHeader("Auth Bearer")}>Auth Bearer</button>
          </div>

          {method !== "GET" && (
            <>
              <label>Body</label>
              <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
              <div className="sample-lists">
                <div>Body samples:</div>
                <button onClick={() => handlePickBody("Login JSON")}>Login JSON</button>
                <button onClick={() => handlePickBody("Empty")}>Empty</button>
              </div>
            </>
          )}

          <div className="row">
            <button onClick={handleSend} disabled={isWorking} className="primary">
              {isWorking ? "Proxying..." : "Send via Proxy"}
            </button>
            <button onClick={handleReset} disabled={isWorking}>
              Reset
            </button>
          </div>
        </div>

        {/* Middle: Terminal */}
        <div className="panel terminal-panel">
          <h2>Terminal</h2>
          <pre ref={terminalRef} className="terminal">{terminal || "Ready.\n"}</pre>
        </div>

        {/* Right: Results */}
        <div className="panel">
          <h2>Proxy Results</h2>
          <div className="result">
            <strong>Intercepted (what proxy saw)</strong>
            <pre>{intercepted || "{ }"}</pre>
          </div>

          <div className="result" style={{ marginTop: 12 }}>
            <strong>Target Response</strong>
            <pre>{response || "{ }"}</pre>
          </div>
        </div>
      </div>

      <div className="footer-note">Educational simulator — do not proxy real third-party production traffic without authorization.</div>
    </div>
  );
}
